import { J2Caution, K1Information } from '@e3dc-react/icons'
import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'

export interface Props {
  variant: 'info' | 'warning' | 'error'
  text: string | React.ReactElement
}

/**
 * NotificationBanner is a simple banner component that displays a message with an icon.
 * It is used to inform users about important information, warnings, or errors.
 * To have a closable banner, use the `Banner` component instead.
 *
 * @see {@link 'https://www.figma.com/design/2XkUgderL8Y8WDXHPhUXNJ?node-id=6046-79&main-component=1&fuid=842299218465702219'}
 */
const NotificationBanner: React.FC<Props> = ({ text, variant }) => {
  const theme = useTheme()
  const backgroundColors = {
    info: 'purple.ultraLight',
    warning: 'warning.light',
    error: 'error.light',
  }

  const iconColors = {
    info: 'purple.main',
    warning: 'warning.main',
    error: 'error.main',
  }

  const backgroundColor = backgroundColors[variant]
  const iconColor = iconColors[variant]
  const smUp = useMediaQuery(theme.breakpoints.up('sm'))

  return (
    <Card
      sx={{
        backgroundColor,
        px: smUp ? 4 : 2,
        py: 2,
        flexDirection: smUp ? 'row' : 'column',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'space-between',
      }}
    >
      {variant === 'info' ? <K1Information sx={{ color: iconColor, flexGrow: 0 }} /> : <J2Caution sx={{ color: iconColor, flexGrow: 0 }} />}
      <Typography sx={{ textAlign: 'center' }} variant="body1">
        {text}
      </Typography>
      {/* Ensure the text is centered in the whole width in not mobile view */}
      {smUp && <Box sx={{ flexGrow: 0, width: '24px' }} />}
    </Card>
  )
}

export default NotificationBanner
